import { useEffect, useRef, useState } from 'react';
import { HISTORY_ZERO_STATE } from '@/constants/HistoryConstants';
import DefaultIcon from '@/assets/icon-default.svg?react';
import FilledHeartIcon from '@/assets/icon-heart-filled.svg?react';
import InfoIcon from '@/assets/icon-info.svg?react';
import useElysiaStore from '@/app/elysia-store';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getFavouriteAppList, toggleAppFavoriteStatus } from '@/services/aiApps';
import OverlayTooltip from './UI/OverlayTooltip';
import { generateChatSessionId } from '@/utils/chat';
import { DEFAULT_ASPECT_RATIO, DEFAULT_IMAGE_GEN_RESOLUTION } from '@/config/settings';
import getIconByAppID from '@/utils/app-icon';

const FavouriteAppList = ({ setIsSideNavOpen }) => {
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setIsPrivateChat = useElysiaStore.getState().setIsPrivateChat;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setAiAppID = useElysiaStore((state) => state.setAiAppId);
  const setClearState = useElysiaStore.getState().setClearState;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setShowScrollIcon = useElysiaStore.getState().setShowScrollIcon;
  const setUploadedFileTitle = useElysiaStore.getState().setUploadedFileTitle;
  const setUploadedFile = useElysiaStore.getState().setUploadedFile;
  const setSources = useElysiaStore.getState().setSources;
  const setSelectedSources = useElysiaStore.getState().setSelectedSources;
  const setWordsCount = useElysiaStore.getState().setWordsCount;
  const setWordCountError = useElysiaStore.getState().setWordCountError;
  const setShowFileToasts = useElysiaStore.getState().setShowFileToasts;
  const setIsChatDisabled = useElysiaStore.getState().setIsChatDisabled;
  const setIsAppTerminated = useElysiaStore.getState().setIsAppTerminated;
  const setBrandDoc = useElysiaStore.getState().setBrandDoc;
  const setCategoryCount = useElysiaStore.getState().setCategoryCount;
  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setIsChatActive = useElysiaStore.getState().setIsChatActive;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setIsAddSourcesActive = useElysiaStore.getState().setIsAddSourcesActive;
  const setImageGenPayload = useElysiaStore.getState().setImageGenPayload;

  const [showAll, setShowAll] = useState<boolean>(false);
  const favouriteAgent = useElysiaStore((state) => state.favouriteAgent);
  const setIsFavouriteAgent = useElysiaStore((state) => state.setFavouriteAgent);
  const appFavourite = useElysiaStore((state) => state.appFavourite);
  const setAppFavourite = useElysiaStore((state) => state.setAppFavourite);
  const setIsAppFileAttached = useElysiaStore.getState().setIsAppFileAttached;
  const setAppFileBadgeTitle = useElysiaStore.getState().setAppFileBadgeTitle;

  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showAll && listRef.current) {
      listRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [showAll]);

  const getIconforAgents = async (app) => {
    const iconList = await Promise.all(
      app?.data.map(async (app) => {
        if (!app.icon_id) return app;

        try {
          return await getIconByAppID(app);
        } catch (error) {
          console.error(`Failed to fetch icon for app ${app.agent_id}`, error);
          return app;
        }
      })
    );
    return iconList;
  };
  useEffect(() => {
    const fetchData = async () => {
      const response = await getFavouriteAppList();
      const app = response?.data;
      const appsWithIcons = await getIconforAgents(app);
      setIsFavouriteAgent([...appsWithIcons]);
    };
    fetchData();
    //eslint-disable-next-line
  }, [appFavourite]);
  const handleAppSelection = (ele) => {
    setBrandDoc?.(false);
    setIsPrivateChat?.(false);
    setChatAgent?.('app-agent');
    setIsAppTerminated(false);
    startNewTopicChat();
    setAiAppID?.(ele?.agent_id);
    if (ele?.agent_id) {
      const url = ele?.redirect_url
        ? `${ele.redirect_url}/?app=${ele.agent_id}`
        : `/?app=${ele.agent_id}`;
      navigate(url, { state: { agent_id: ele.agent_id } });
      setIsSideNavOpen(true);
    }
  };

  const startNewTopicChat = () => {
    setIsAddSourcesActive?.(false);
    setShowScrollIcon?.(false);
    useElysiaStore.setState({ isStreamingStopped: false });
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    setIsChatActive?.(false);
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    setPromptInput('');
    setSessionPath?.('');
    setIsRestrictedChatSessionStatus?.('');
    setUploadedFileTitle?.('');
    setUploadedFile?.(null);
    setIsAppFileAttached(false);
    setSources?.([]);
    setSelectedSources?.([]);
    setBrandDoc(false);
    setAppFileBadgeTitle('');
    setWordsCount?.(0);
    setWordCountError?.(false);
    setShowFileToasts?.(false);
    setIsChatDisabled?.(false);
    setImageGenPayload({
      aspect_ratio: DEFAULT_ASPECT_RATIO,
      color_palette: [],
      starter_image_id: '',
      image_quality: DEFAULT_IMAGE_GEN_RESOLUTION,
    });
    setCategoryCount({});
  };

  const handleViewMore = () => {
    setShowAll(!showAll);
  };

  const handleFavorite = async (ele: FavouriteAgentDetails) => {
    const payload = { agent_id: ele.agent_id, is_favourite: !ele.is_favourite };
    const response = await toggleAppFavoriteStatus(payload);
    if (response?.data?.status) {
      setAppFavourite(payload);
    }
  };

  return (
    <div className="favorite-apps">
      <div
        ref={listRef}
        className={`favorite-list mt-2 me-2 ${showAll ? 'expand overflow-auto' : 'collapsed'}`}
      >
        {favouriteAgent?.map((ele: FavouriteAgentDetails) => {
          const isDisbale = ele?.agent_status === 'unpublished';
          return (
            <div key={ele.agent_id} className="apps-list d-flex align-items-center w-100">
              <Button
                role="button"
                className={`app-button light-without-border fw-semibold d-flex align-items-center flex-grow-1 ${
                  isDisbale ? 'app-disabled' : ''
                }`}
                onClick={() => {
                  if (!isDisbale) {
                    handleAppSelection(ele);
                  }
                }}
              >
                {/* <BrandAgentIcon className="/> */}
                {ele?.icon ? (
                  <img src={ele.icon} alt="app-icon" className="app-icon flex-shrink-0" />
                ) : (
                  <DefaultIcon className="app-icon flex-shrink-0" />
                )}

                <span className="ms-2 me-2 app-name" title={ele?.agent_name}>
                  {ele.agent_name}
                </span>
                {ele?.agent_status == 'unpublished' && (
                  <div>
                    <OverlayTooltip
                      tooltipText="This App is unavailable"
                      buttonContent={<InfoIcon />}
                      buttonClassName={`unselected-chat-action-button me-1 like-button like-icon   `}
                      ariaLabel="information"
                      testId="app-info"
                      tooltipClassName="tooltip-sidebar"
                      placement="bottom"
                    />
                  </div>
                )}
              </Button>
              <div
                className={`ms-auto heart-icon me-2 flex-shrink-0 ${isDisbale ? 'opacity-50' : ' '} `}
                onClick={() => handleFavorite(ele)}
              >
                <FilledHeartIcon />
              </div>
            </div>
          );
        })}
      </div>

      {favouriteAgent.length > 5 && (
        <Button variant="link" className="view-more-btn" onClick={handleViewMore}>
          {showAll ? 'view less' : 'view more'}
        </Button>
      )}
    </div>
  );
};

export default FavouriteAppList;
